import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowRight, Award } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useStartConsultation } from "@/components/Layout";
import { ReadinessBadge } from "@/components/Badges";
import { Disclosure } from "@/components/Brand";
import { QuickStartTips } from "@/components/QuickStartTips";
import { fmtDate as fmtDay } from "@/components/PartnerCode";
import { KitCard } from "@/components/kit/KitCard";
import { useKitQuery } from "@/components/kit/useKit";

const HERO_IMG = "/images/consult-hero.webp";

function BrowMark() {
  return (
    <svg width="34" height="14" viewBox="0 0 34 14" fill="none" aria-hidden="true">
      <path d="M1 10C7 3 17 1.5 24 6.5" stroke="#C5A059" strokeWidth="2.4" strokeLinecap="round" />
      <path d="M29 2.5l.9 2.6 2.6.9-2.6.9-.9 2.6-.9-2.6-2.6-.9 2.6-.9z" stroke="#C5A059" strokeWidth="1" strokeLinejoin="round" />
    </svg>
  );
}
const LOVE = [
  ["L", "Layers", "Tissue state, residual pigment, history of tattooing, scarring or removal."],
  ["O", "Optics", "Bottle vs implanted vs fresh vs healed perceived colour; melanin and scattering."],
  ["V", "Variables", "Technique, density, CI chemistry, inventory, healing history, region."],
  ["E", "Evaluation", "Readiness screen, multiple defensible options, uncertainty — you decide."],
];

export const fmtDate = (iso) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

export default function Dashboard() {
  const { user } = useAuth();
  const start = useStartConsultation();
  const { data: consults = [] } = useQuery({ queryKey: ["consultations"], queryFn: async () => (await api.get("/consultations")).data });
  const { data: kit = [], isLoading, isError } = useKitQuery();
  const kitLoading = isLoading || isError;

  return (
    <div className="mx-auto max-w-6xl px-6 py-10 sm:px-10 lg:py-14">
      <p className="ig-rise font-mono-ig text-xs uppercase tracking-[0.3em] text-[#C5A059]">Studio dashboard</p>
      <h1 className="ig-rise ig-d1 mt-3 text-4xl font-semibold text-[#2C1810] sm:text-5xl lg:text-6xl">
        Good to see you{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.
      </h1>
      {user?.billing?.partner?.active && (
        <Link to="/billing" data-testid="dashboard-partner-badge" title={`Complimentary access through ${fmtDay(user.billing.partner.end_date)}`}
          className="ig-rise ig-d2 mt-4 inline-flex items-center gap-2 rounded-full border border-[#C5A059] bg-gradient-to-r from-[#FFF7E6] to-[#F7EEDD] px-4 py-1.5 text-xs font-semibold tracking-wide text-[#6B4E1F] shadow-[0_4px_14px_rgba(197,160,89,0.18)] transition-transform hover:-translate-y-0.5">
          <Award size={14} className="text-[#C5A059]" /> PMU Accelerator Partner
          <span className="font-normal text-[#8A7466]">· through {fmtDay(user.billing.partner.end_date)}</span>
        </Link>
      )}
      <QuickStartTips user={user} kitCount={kit.length} loading={kitLoading} />
      <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div data-testid="dashboard-consult-hero" className="ig-rise ig-d2 relative flex flex-col overflow-hidden rounded-3xl bg-[#2E1A11] text-[#FDFBF7] lg:col-span-12">
          <img src={HERO_IMG} alt="Amber pigment bottles in front of a softly lit brow and eye" className="absolute inset-y-0 right-0 hidden h-full w-[62%] object-cover object-[22%_center] [mask-image:linear-gradient(to_right,transparent_0%,black_38%)] md:block lg:w-[52%] lg:object-center" />
          <div className="relative flex flex-1 flex-col justify-center p-8 sm:p-10 md:max-w-[56%] lg:min-h-[400px] lg:max-w-[62%] lg:px-14 lg:py-12">
            <p className="flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.32em] text-[#C5A059]" data-testid="dashboard-consult-eyebrow">
              <BrowMark /> Guided L.O.V.E. consultation
            </p>
            <h2 className="mt-5 font-serif text-[34px] font-medium leading-[1.05] text-[#FDFBF7] sm:text-[40px] lg:text-[50px] xl:text-[56px]">
              Beautiful healed results start with <em className="font-normal italic text-[#C5A059]">informed</em> pigment choices.
            </h2>
            <p className="mt-5 max-w-md text-[15px] leading-relaxed text-[#EADFD2] lg:max-w-xl lg:text-lg">Explore pigment options for your client’s skin, your technique, and the results you’re working toward.</p>
            <button onClick={start} data-testid="dashboard-start-consultation-btn" className="mt-8 inline-flex h-14 w-fit items-center gap-3 rounded-full bg-[#C5A059] px-8 text-base font-semibold text-[#2C1810] transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-[#d4b273]">
              Start my consultation <ArrowRight size={18} />
            </button>
          </div>
          <div className="relative -mt-4 h-56 md:hidden">
            <img src={HERO_IMG} alt="" className="h-full w-full object-cover object-[45%_center]" />
            <div className="absolute inset-0 bg-gradient-to-b from-[#2E1A11] to-transparent to-50%" />
          </div>
        </div>
      </div>
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="ig-rise ig-d3 min-w-0 rounded-3xl border border-[#E6DBCF] bg-[#FDFBF7] p-8 lg:col-span-7">
          <div className="flex items-baseline justify-between">
            <h3 className="text-2xl font-semibold text-[#2C1810]">Recent consultations</h3>
            <Link to="/history" className="text-xs uppercase tracking-[0.2em] text-[#6E5549] hover:text-[#2C1810]" data-testid="dashboard-view-all-history">View all</Link>
          </div>
          <div className="mt-5 divide-y divide-[#E6DBCF]">
            {consults.slice(0, 5).map((c) => (
              <Link key={c.id} to={`/consult/${c.id}`} data-testid={`dashboard-consult-${c.id}`} className="flex items-center gap-4 py-3.5 transition-colors hover:text-[#C5A059]">
                <span className="min-w-0 flex-1 truncate text-sm text-[#2C1810]">{c.title}</span>
                <ReadinessBadge status={c.readiness} />
                <span className="shrink-0 text-xs text-[#A88B7D]">{fmtDate(c.updated_at)}</span>
              </Link>
            ))}
            {!consults.length && <p className="py-6 text-sm italic text-[#A88B7D]" data-testid="dashboard-no-consults">No consultations yet — your first case starts with one question.</p>}
          </div>
        </div>
        <KitCard />
      </div>
      <div className="ig-rise ig-d4 mt-6 rounded-3xl border border-[#E6DBCF] bg-[#F6F1EA] p-8" data-testid="dashboard-love-framework">
        <h3 className="text-2xl font-semibold text-[#2C1810]">The L.O.V.E. framework</h3>
        <div className="mt-5 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {LOVE.map(([l, n, d]) => (
            <div key={l} className="flex gap-4">
              <span className="font-serif text-3xl italic leading-none text-[#C5A059]">{l}</span>
              <div><p className="text-sm font-semibold text-[#2C1810]">{n}</p><p className="text-xs leading-relaxed text-[#6E5549]">{d}</p></div>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-10 max-w-3xl"><Disclosure /></div>
    </div>
  );
}
